import { FormEvent, useEffect, useState } from 'react';
import { MenuItem, Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useRecoilValue } from 'recoil';
import { useTranslation } from '../../../contexts/TranslationContext';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { DeviceWarningLevelPercentage } from './DeviceWarningLevelPercentage';
import { DeviceWarningLevelMM } from './DeviceWarningLevelMM';
import { DeviceWarningLevelFtIn } from './DeviceWarningLevelFtIn';
import { CheckPermission } from '../CheckPermission';
import { device_controller } from '../../../controllers/device_controller';
import { DisclaimerText, ErrorText } from '../MHM/MHMTypography';
import { DeviceInfoSection } from './DeviceInfoSection';
import { MHMSelect } from '../MHM/MHMSelect';

type Props = {
  canEditSession: boolean;
};

export function DeviceWarningLevel({ canEditSession }: Props) {
  const { t, language } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const canManageDevices = useRecoilValue(canManageDevicesSelector);
  const [warningMode, setWarningMode] = useState<'percentage' | 'mm'>(
    'percentage',
  );
  const [editingWarningLevelPercentage, setEditingWarningLevelPercentage] =
    useState(selectedDevice?.warning_level_percentage ?? 0);
  const [errorMessage, setErrorMessage] = useState<
    [string, Record<string, string>]
  >(['', {}]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPristine, setIsPristine] = useState(true);

  function handleChange(
    value: number,
    errorMessage: [string, Record<string, string>],
  ) {
    setEditingWarningLevelPercentage(value);
    setErrorMessage(errorMessage);
    setIsPristine(false);
  }

  async function handleSubmit(e: FormEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedDevice || errorMessage[0]) {
      return;
    }

    setIsSubmitting(true);
    try {
      await device_controller.change_device_warning_level_percentage(
        selectedDevice.device_id,
        editingWarningLevelPercentage,
      );
      enqueueSnackbar(
        t(
          'Updated settings will be applied the next time the device transmits',
        ),
        {
          variant: 'success',
          autoHideDuration: 3000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    setEditingWarningLevelPercentage(
      selectedDevice?.warning_level_percentage ?? 0,
    );
    setErrorMessage(['', {}]);
    setIsSubmitting(false);
    setIsPristine(true);
  }, [selectedDevice]);

  if (!selectedDevice) {
    return null;
  }

  let WarningInputComponent;
  if (warningMode === 'mm') {
    if (language === 'en-US') {
      WarningInputComponent = DeviceWarningLevelFtIn;
    } else {
      WarningInputComponent = DeviceWarningLevelMM;
    }
  } else {
    WarningInputComponent = DeviceWarningLevelPercentage;
  }

  return (
    <DeviceInfoSection.Entry
      label={t('Warning Threshold:')}
      direction="column"
      gap={1}
      component="form"
      onSubmit={e => handleSubmit(e)}
    >
      <Stack direction="row" gap={1}>
        <CheckPermission hasPermission={canManageDevices}>
          <WarningInputComponent
            canEditSession={canEditSession}
            defaultWarningPercentage={editingWarningLevelPercentage}
            onChange={handleChange}
          />
        </CheckPermission>
        <MHMSelect
          value={warningMode}
          size="small"
          sx={{ alignSelf: 'flex-start' }}
          onChange={e => setWarningMode(e.target.value as 'percentage' | 'mm')}
        >
          <MenuItem value="percentage">{t('Percentage')}</MenuItem>
          <MenuItem value="mm">{t('Millimeters_ft_in')}</MenuItem>
        </MHMSelect>
      </Stack>
      <ErrorText>{t(...errorMessage)}</ErrorText>
      <DisclaimerText component="div">
        {t('The threshold level at which')}
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>{t('The device sends alerts to subscribed users')}</li>
          <li>
            {t('More frequent transmissions engage. (High Warning Mode)')}
          </li>
        </ul>
        {language === 'en-US'
          ? t('Note: 0%/0 ft 0 in is at invert level.')
          : t('Note: 0%/0mm is at invert level.')}
      </DisclaimerText>
      <DeviceInfoSection.Button
        disabled={!canEditSession || Boolean(errorMessage[0]) || isPristine}
        loading={isSubmitting}
        type="submit"
        sx={{ width: 'fit-content' }}
      >
        {t('Save')}
      </DeviceInfoSection.Button>
    </DeviceInfoSection.Entry>
  );
}
